import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, School, Trash2, Pencil, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LessonRow } from "@/pages/enseignement/AgendaTeachingPage";
import { RESOURCE_ICONS, isMediaResource, openTeachingResource, ResourcePlayerDialog } from "@/pages/enseignement/teachUtils";

// Page d'une classe : aperçu + membres (infos limitées). Gestion réservée à l'enseignant.
export default function ClassePage() {
  const { classId } = useParams();
  const { tspace, lang, t } = useAuth();
  const isTeacher = tspace?.my_role === "teacher";
  const navigate = useNavigate();
  const [cls, setCls] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [resources, setResources] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [player, setPlayer] = useState(null);
  const openRes = (r) => (isMediaResource(r) ? setPlayer(r) : openTeachingResource(r));
  const [notFound, setNotFound] = useState(false);
  const [students, setStudents] = useState([]);
  const [addId, setAddId] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "", instrument: "" });
  const [delOpen, setDelOpen] = useState(false);

  const load = () => api.get(`/teaching/classes/${classId}`).then((r) => setCls(r.data)).catch(() => setNotFound(true));
  useEffect(() => {
    load();
    if (isTeacher) api.get("/teaching/students").then((r) => setStudents(r.data.students)).catch(() => {});
    // Cours de la classe (visibles par l'enseignant et les membres)
    api.get("/teaching/lessons").then((r) => setLessons(r.data.lessons.filter((l) => l.class_id === classId))).catch(() => {});
    // Ressources partagées avec la classe
    api.get("/teaching/resources").then((r) => setResources(r.data.resources.filter((x) =>
      isTeacher ? (x.class_ids || []).includes(classId) : (x.via_class_ids || []).includes(classId)
    ))).catch(() => {});
    // Devoirs de la classe
    api.get("/teaching/assignments").then((r) => setAssignments(r.data.assignments.filter((a) => a.class_id === classId))).catch(() => {});
  }, [classId]);

  const addMember = async () => {
    if (!addId) return;
    try {
      const r = await api.post(`/teaching/classes/${classId}/members`, { student_user_id: addId });
      setCls(r.data);
      setAddId("");
    } catch (e) { toast.error(e.friendly); }
  };

  const removeMember = async (sid) => {
    try {
      const r = await api.delete(`/teaching/classes/${classId}/members/${sid}`);
      setCls(r.data);
    } catch (e) { toast.error(e.friendly); }
  };

  const saveEdit = async () => {
    try {
      const r = await api.put(`/teaching/classes/${classId}`, editForm);
      setCls(r.data);
      setEditOpen(false);
      toast.success(t("teach.saved"));
    } catch (e) { toast.error(e.friendly); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/teaching/classes/${classId}`);
      navigate("/enseignement/classes");
    } catch (e) { toast.error(e.friendly); }
  };

  if (notFound) return <p className="text-sm t-secondary py-10 text-center" data-testid="class-not-found">404</p>;
  if (!cls) return <p className="t-secondary text-sm" data-testid="class-loading">{t("common.loading")}</p>;

  const memberIds = new Set((cls.members || []).map((m) => m.student_user_id));
  const addable = students.filter((s) => s.status === "ACTIVE" && !memberIds.has(s.student_user_id));

  return (
    <div className="space-y-4 max-w-2xl" data-testid="class-page">
      <button onClick={() => navigate("/enseignement/classes")} className="text-sm t-secondary hover:text-white flex items-center gap-1" data-testid="class-back">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>

      <div className="app-card p-6" data-testid="class-overview">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2" data-testid="class-name">
              <School className="h-5 w-5 brand shrink-0" /> {cls.name}
            </h2>
            {cls.instrument && <p className="text-sm t-secondary mt-1">{cls.instrument}</p>}
            {cls.description && <p className="text-sm t-secondary mt-1">{cls.description}</p>}
          </div>
          {isTeacher && (
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditForm({ name: cls.name, description: cls.description || "", instrument: cls.instrument || "" }); setEditOpen(true); }} data-testid="class-edit-button">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400" onClick={() => setDelOpen(true)} data-testid="class-delete-button">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="app-card p-6 space-y-3" data-testid="class-members-card">
        <h3 className="font-heading font-bold">{t("teach.members")} — {t(cls.member_count > 1 ? "teach.member_count_p" : "teach.member_count_s", { n: cls.member_count })}</h3>
        {(cls.members || []).length === 0 ? (
          <p className="text-sm t-secondary">{t("teach.no_students")}</p>
        ) : (
          <div className="space-y-1" data-testid="class-members-list">
            {cls.members.map((m) => (
              <div key={m.student_user_id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.03]" data-testid={`class-member-${m.student_user_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{m.prenom} {m.nom}</p>
                  {m.instrument && <p className="text-xs t-secondary">{m.instrument}</p>}
                </div>
                {isTeacher && (
                  <button onClick={() => removeMember(m.student_user_id)} className="t-secondary hover:text-rose-400 p-1" data-testid={`class-member-remove-${m.student_user_id}`} aria-label={t("teach.remove_member")}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {isTeacher && addable.length > 0 && (
          <div className="flex gap-2 pt-2 border-t border-app">
            <Select value={addId} onValueChange={setAddId}>
              <SelectTrigger className="bg-transparent border-app" data-testid="class-add-member-select"><SelectValue placeholder={t("teach.add_member")} /></SelectTrigger>
              <SelectContent>
                {addable.map((s) => <SelectItem key={s.student_user_id} value={s.student_user_id}>{s.prenom} {s.nom}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button className="brand-bg text-white shrink-0" onClick={addMember} disabled={!addId} data-testid="class-add-member-confirm">
              <UserPlus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="app-card p-6 space-y-2" data-testid="class-lessons-card">
        <h3 className="font-heading font-bold">{t("teach.class_lessons")}</h3>
        {lessons.length === 0 ? (
          <p className="text-sm t-secondary" data-testid="class-lessons-empty">{t("teach.no_lessons")}</p>
        ) : (
          lessons.map((l) => <LessonRow key={l.id} l={l} lang={lang} t={t} canEdit={false} />)
        )}
      </div>

      <div className="app-card p-6 space-y-2" data-testid="class-resources-card">
        <h3 className="font-heading font-bold">{t("teach.class_resources")}</h3>
        {resources.length === 0 ? (
          <p className="text-sm t-secondary" data-testid="class-resources-empty">{t("teach.no_resources")}</p>
        ) : (
          resources.map((r) => {
            const Icon = RESOURCE_ICONS[r.type] || RESOURCE_ICONS.document;
            return (
              <button key={r.id} onClick={() => openRes(r)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-left" data-testid={`class-resource-${r.id}`}>
                <Icon className="h-4 w-4 brand shrink-0" />
                <span className="text-sm font-medium truncate">{r.title}</span>
                <span className="text-[10px] t-secondary ml-auto shrink-0">{t(`teach.type_${r.type}`)}</span>
              </button>
            );
          })
        )}
      </div>

      <div className="app-card p-6 space-y-2" data-testid="class-assignments-card">
        <h3 className="font-heading font-bold">{t("teach.nav_assignments")}</h3>
        {assignments.length === 0 ? (
          <p className="text-sm t-secondary" data-testid="class-assignments-empty">{t("teach.no_assignments")}</p>
        ) : (
          assignments.map((a) => (
            <div key={a.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03]" data-testid={`class-assignment-${a.id}`}>
              <span className="text-sm font-medium truncate flex-1">{a.title}</span>
              <span className="text-[10px] t-secondary shrink-0">{a.due_date || t("teach.no_due")}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${
                (isTeacher ? a.done_count === a.total_count && a.total_count > 0 : a.my_done)
                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                  : "bg-amber-500/15 text-amber-400 border-amber-500/30"
              }`}>
                {t(isTeacher ? "teach.done_count" : (a.my_done ? "teach.done_badge" : "teach.todo_badge"), isTeacher ? { done: a.done_count, total: a.total_count } : {})}
              </span>
            </div>
          ))
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="card-bg border-app" data-testid="class-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("teach.add_class")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.class_name")}</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="class-edit-name-input" />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.description")}</Label>
              <Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="class-edit-desc-input" />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("param.instrument_one")}</Label>
              <Input value={editForm.instrument} onChange={(e) => setEditForm({ ...editForm, instrument: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="class-edit-instrument-input" />
            </div>
            <Button className="w-full brand-bg text-white" onClick={saveEdit} disabled={!editForm.name.trim()} data-testid="class-edit-save">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={delOpen}
        title={t("teach.delete_class")}
        description={t("teach.delete_class_desc")}
        onConfirm={doDelete}
        onClose={() => setDelOpen(false)}
        testid="class-delete-confirm"
      />
      {player && <ResourcePlayerDialog resource={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}
